import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ehAdminSistema, podeAcessarRota } from '../auth/perfil-acesso';
import { usePlano } from '../hooks/usePlano';

interface DashboardPageProps {
  modo?: 'tenant' | 'sistema';
}

interface CrecheResumo {
  id: number;
  capacidade_instalada?: number;
  gestores?: unknown[];
}

interface IAAlertaResumo {
  id: number;
  tipo?: string;
  status?: string;
}

interface IAInsightResumo {
  id: number;
  descricao?: string;
  criado_em?: string;
}

export default function DashboardPage({ modo = 'tenant' }: DashboardPageProps) {
  const { usuario } = useAuth();
  const { plano } = usePlano();
  const navigate = useNavigate();
  const adminSistema = ehAdminSistema(usuario?.tipo_usuario);
  const [creches, setCreches] = useState<CrecheResumo[]>([]);
  const [turmas, setTurmas] = useState<{ id: number }[]>([]);
  const [alunos, setAlunos] = useState<{ id: number }[]>([]);
  const [mensalidades, setMensalidades] = useState<{ status?: string }[]>([]);
  const [iaAlertas, setIaAlertas] = useState<IAAlertaResumo[]>([]);
  const [iaInsights, setIaInsights] = useState<IAInsightResumo[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const [rCreches, rTurmas, rAlunos, rMensalidades, rIaAlertas, rIaInsights] = await Promise.allSettled([
          podeAcessarRota(usuario?.tipo_usuario, '/app/creches')
            ? api.get<CrecheResumo[]>('/creches')
            : Promise.resolve({ data: [] as CrecheResumo[] }),
          podeAcessarRota(usuario?.tipo_usuario, '/app/turmas')
            ? api.get<{ id: number }[]>('/turmas')
            : Promise.resolve({ data: [] as { id: number }[] }),
          api.get<{ id: number }[]>('/alunos'),
          podeAcessarRota(usuario?.tipo_usuario, '/app/financeiro')
            ? api.get<{ status?: string }[]>('/financeiro/mensalidades')
            : Promise.resolve({ data: [] as { status?: string }[] }),
          podeAcessarRota(usuario?.tipo_usuario, '/app/ia')
            ? api.get<IAAlertaResumo[]>('/ia/alertas')
            : Promise.resolve({ data: [] as IAAlertaResumo[] }),
          podeAcessarRota(usuario?.tipo_usuario, '/app/ia')
            ? api.get<IAInsightResumo[]>('/ia/insights')
            : Promise.resolve({ data: [] as IAInsightResumo[] }),
        ]);

        setCreches(rCreches.status === 'fulfilled' ? (rCreches.value.data || []) : []);
        setTurmas(rTurmas.status === 'fulfilled' ? (rTurmas.value.data || []) : []);
        setAlunos(rAlunos.status === 'fulfilled' ? (rAlunos.value.data || []) : []);
        setMensalidades(rMensalidades.status === 'fulfilled' ? (rMensalidades.value.data || []) : []);
        setIaAlertas(rIaAlertas.status === 'fulfilled' ? (rIaAlertas.value.data || []) : []);
        setIaInsights(rIaInsights.status === 'fulfilled' ? (rIaInsights.value.data || []) : []);
      } catch {
        setCreches([]);
        setTurmas([]);
        setAlunos([]);
        setMensalidades([]);
        setIaAlertas([]);
        setIaInsights([]);
      }
    }

    load();
  }, [usuario?.tipo_usuario]);

  const capacidadeInstaladaTotal = useMemo(
    () => creches.reduce((acc, c) => acc + Number(c.capacidade_instalada || 0), 0),
    [creches],
  );

  const gestoresCadastrados = useMemo(
    () => creches.reduce((acc, c) => acc + (Array.isArray(c.gestores) ? c.gestores.length : 0), 0),
    [creches],
  );

  const taxaOcupacao = capacidadeInstaladaTotal > 0
    ? `${Math.min(100, Math.round((alunos.length / capacidadeInstaladaTotal) * 100))}%`
    : '0%';

  const unidadeConfigurada = turmas.length > 0;
  const alunosImportados = alunos.length > 0;
  const progressoOnboarding = Math.round(
    (([true, unidadeConfigurada, alunosImportados, false].filter(Boolean).length) / 4) * 100,
  );

  const inadimplentes = mensalidades.filter((m) => (m.status || '').toLowerCase() === 'vencido').length;
  const alertasCriticosAbertos = iaAlertas.filter(
    (a) => (a.tipo || '').toLowerCase() === 'critico' && (a.status || '').toLowerCase() !== 'resolvido',
  ).length;
  const ultimosInsights = [...iaInsights]
    .sort((a, b) => String(b.criado_em || '').localeCompare(String(a.criado_em || '')))
    .slice(0, 3);

  const cards = useMemo(() => {
    return [
      { key: 'creches', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/creches'), className: 'card-stat', icon: '🏫', label: 'Creches', valor: String(creches.length) },
      { key: 'turmas', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/turmas'), className: 'card-stat', icon: '🎓', label: 'Turmas', valor: String(turmas.length) },
      { key: 'alunos', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/alunos'), className: 'card-stat', icon: '👶', label: 'Alunos', valor: String(alunos.length) },
      { key: 'inadimplencia', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/financeiro'), className: 'card-stat', icon: '💰', label: 'Inadimplência', valor: String(inadimplentes) },
      { key: 'capacidade', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/creches'), className: 'card-stat', icon: '🏗️', label: 'Capacidade Instalada', valor: String(capacidadeInstaladaTotal) },
      { key: 'ocupacao', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/creches'), className: 'card-stat', icon: '📊', label: 'Taxa de Ocupação', valor: taxaOcupacao },
      { key: 'gestores', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/creches'), className: 'card-stat', icon: '👔', label: 'Gestores Cadastrados', valor: String(gestoresCadastrados) },
      { key: 'ia-alertas', allow: podeAcessarRota(usuario?.tipo_usuario, '/app/ia'), className: 'card-stat card-stat-ia-alerta', icon: '🚨', label: 'IA: Alertas Críticos', valor: String(alertasCriticosAbertos) },
      {
        key: 'ia-insights',
        allow: podeAcessarRota(usuario?.tipo_usuario, '/app/ia'),
        className: 'card-stat card-stat-ia-insight',
        icon: '🧠',
        label: 'IA: Últimos Insights',
        valor: String(ultimosInsights.length),
        ajuda: ultimosInsights[0]?.descricao ? ultimosInsights[0].descricao.slice(0, 80) : 'Sem insights recentes.',
      },
    ].filter((item) => item.allow);
  }, [
    usuario?.tipo_usuario,
    creches.length,
    turmas.length,
    alunos.length,
    inadimplentes,
    capacidadeInstaladaTotal,
    taxaOcupacao,
    gestoresCadastrados,
    alertasCriticosAbertos,
    ultimosInsights,
  ]);

  return (
    <div>
      <h2 className="pagina-titulo">{modo === 'sistema' ? 'Dashboard do Sistema SaaS' : 'Dashboard da Creche'}</h2>
      <p className="pagina-subtitulo">
        Bem-vindo, <strong>{usuario?.email}</strong>!{' '}
        {adminSistema
          ? 'Você está no contexto de administração global (SaaS).'
          : 'Você está no contexto administrativo da sua creche (tenant).'}
      </p>

      {/* Seção Onboarding Plug & Play (Início Imediato) */}
      {/* O plano vem da ASSINATURA da creche. Antes lia `usuario.id_plano`, que
          não existe, e por isso toda creche era saudada como "plano BRONZE" —
          inclusive quem paga Prata. Enquanto carrega, `plano` é null e o bloco
          não aparece, em vez de aparecer com o nome errado. */}
      {plano && (
        <div style={{ marginBottom: '30px', padding: '20px', borderRadius: '15px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div>
              <h2 style={{ margin: 0 }}>🚀 Bem-vindo à sua nova Gestão!</h2>
              <p style={{ opacity: 0.9 }}>Seu plano <strong>{plano}</strong> foi ativado. Complete os passos abaixo para iniciar a operação.</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{progressoOnboarding}%</span>
              <div style={{ width: '120px', height: '8px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '4px', marginTop: '5px' }}>
                <div style={{ width: `${progressoOnboarding}%`, height: '100%', backgroundColor: '#00e676', borderRadius: '4px' }}></div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '10px' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>✅</div>
              <strong style={{ display: 'block' }}>Contrato Assinado</strong>
              <small>Formalização concluída</small>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '10px' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>{unidadeConfigurada ? '✅' : '❌'}</div>
              <strong style={{ display: 'block' }}>Configurar Unidade</strong>
              <small>Salas, Berçários e RH</small>
              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                <button style={{ flex: 1, padding: '5px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/app/salas')}>Salas</button>
                <button style={{ flex: 1, padding: '5px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/app/funcionarios')}>RH</button>
              </div>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '10px' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>{alunosImportados ? '✅' : '❌'}</div>
              <strong style={{ display: 'block' }}>Importar Alunos</strong>
              <small>Cadastrar primeiras crianças</small>
              <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                <button style={{ flex: 1, padding: '5px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/app/importar')}>Importar planilha</button>
                <button style={{ flex: 1, padding: '5px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/app/alunos')}>Manual</button>
              </div>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '10px' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
              <strong style={{ display: 'block' }}>Treinamento da Assistente</strong>
              <small>Conheça e batize sua assistente</small>
              <button style={{ marginTop: '10px', width: '100%', padding: '5px', borderRadius: '5px', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/app/ia')}>Iniciar</button>
            </div>
          </div>
        </div>
      )}

      <div className="cards-grid">
        {cards.map((card) => (
          <div key={card.key} className={card.className}>
            <span className="card-stat-icon">{card.icon}</span>
            <div>
              <p className="card-stat-label">{card.label}</p>
              <p className="card-stat-valor">{card.valor}</p>
              {card.ajuda ? <p className="texto-ajuda">{card.ajuda}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
